import { useState, useEffect, useCallback } from 'react';
import { auditAPI } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, ClipboardList, ShieldAlert } from 'lucide-react';

const ENTITY_TYPES = ['USER', 'ROLE', 'CATEGORY', 'SKU', 'TARGET', 'PURCHASE', 'SETTINGS'];

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('all');
  const [accessDenied, setAccessDenied] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (entityFilter && entityFilter !== 'all') {
        params.entity_type = entityFilter;
      }
      const response = await auditAPI.list(params);
      setLogs(response.data);
      setAccessDenied(false);
    } catch (error) {
      if (error?.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error('Failed to load audit logs');
      }
    } finally {
      setLoading(false);
    }
  }, [entityFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const getActionColor = (action) => {
    if (action.includes('CREATE')) return 'bg-emerald-100 text-emerald-800';
    if (action.includes('UPDATE') || action.includes('CHANGE')) return 'bg-blue-100 text-blue-800';
    if (action.includes('DELETE')) return 'bg-red-100 text-red-800';
    if (action.includes('APPROVAL')) return 'bg-amber-100 text-amber-800';
    return 'bg-gray-100 text-gray-600';
  };

  if (accessDenied) {
    return (
      <div className="space-y-6" data-testid="audit-log-page">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Audit Log</h1>
          <p className="text-muted-foreground">Track all system changes and actions</p>
        </div>
        <Card className="rounded-sm">
          <CardContent className="p-12 text-center" data-testid="audit-log-access-denied">
            <ShieldAlert className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="font-medium">You don't have access to this page</p>
            <p className="text-muted-foreground text-sm mt-1">Audit Log access is restricted to Admins and Super Admins.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Audit Log</h1>
        <p className="text-muted-foreground">Track all system changes and actions</p>
      </div>

      {/* Filters */}
      <Card className="rounded-sm">
        <CardContent className="p-4">
          <Select value={entityFilter} onValueChange={setEntityFilter}>
            <SelectTrigger className="w-48 rounded-sm">
              <SelectValue placeholder="All Entities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Entities</SelectItem>
              {ENTITY_TYPES.map((type) => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="rounded-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="font-medium text-xs uppercase">Timestamp</TableHead>
                <TableHead className="font-medium text-xs uppercase">Action</TableHead>
                <TableHead className="font-medium text-xs uppercase">Entity</TableHead>
                <TableHead className="font-medium text-xs uppercase">User</TableHead>
                <TableHead className="font-medium text-xs uppercase">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <ClipboardList className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
                    <p className="text-muted-foreground">No audit logs found</p>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id} className="table-row-hover" data-testid={`audit-row-${log.id}`}>
                    <TableCell className="text-sm">{formatDateTime(log.timestamp)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`rounded-sm text-xs ${getActionColor(log.action)}`}>
                        {log.action.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="rounded-sm text-xs">
                        {log.entity_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{log.user_email}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[300px]">
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">
                        {JSON.stringify(log.details).substring(0, 100)}
                        {JSON.stringify(log.details).length > 100 ? '...' : ''}
                      </code>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
